/**
 * useSortableTable — generic 3-state sortable table hook.
 *
 * States cycle on header click:
 *   default → asc → desc → default (loops back)
 *
 * Usage:
 *   const { sortedItems, sortKey, sortDir, requestSort } = useSortableTable(employees);
 *
 *   <TableHeader>
 *     <SortableHead sortKey="cognome" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
 *       Cognome
 *     </SortableHead>
 *   </TableHeader>
 *
 * The hook is intentionally generic — it works with any list of objects,
 * inferring types automatically (strings, numbers, ISO dates, nulls last).
 * Pass an `accessor(item) => value` function via `customAccessors` for fields
 * that aren't directly on the item (e.g. nested or computed values).
 */
import { useMemo, useState, useCallback } from "react";

/** Returns -1 / 0 / 1 — nulls/undefined always last */
const compareValues = (a, b) => {
    const aNil = a === null || a === undefined || a === "";
    const bNil = b === null || b === undefined || b === "";
    if (aNil && bNil) return 0;
    if (aNil) return 1;   // nulls last
    if (bNil) return -1;

    if (typeof a === "number" && typeof b === "number") {
        return a - b;
    }
    // Try numeric coercion (matricola "0629" vs 629)
    const an = typeof a === "string" ? Number(a) : NaN;
    const bn = typeof b === "string" ? Number(b) : NaN;
    if (!Number.isNaN(an) && !Number.isNaN(bn)) {
        return an - bn;
    }
    // Date-like ISO strings sort lexically already; locale string compare otherwise
    return String(a).localeCompare(String(b), "it", { numeric: true, sensitivity: "base" });
};

export const useSortableTable = (items, options = {}) => {
    const { customAccessors = {}, initialSort } = options;
    // Single state object so the 3-state cycle is atomic (no batched-update races).
    const [sort, setSort] = useState(() => (initialSort?.key ? { key: initialSort.key, dir: initialSort.dir === "desc" ? "desc" : "asc" } : { key: null, dir: "asc" }));

    const requestSort = useCallback((key) => {
        setSort((curr) => {
            if (curr.key !== key) {
                // First click on this column → asc
                return { key, dir: "asc" };
            }
            if (curr.dir === "asc") return { key, dir: "desc" };
            // 3rd click: reset to default (no sort)
            return { key: null, dir: "asc" };
        });
    }, []);

    const sortedItems = useMemo(() => {
        if (!sort.key || !Array.isArray(items)) return items;
        const accessor = customAccessors[sort.key] || ((it) => it?.[sort.key]);
        const arr = [...items];
        arr.sort((a, b) => compareValues(accessor(a), accessor(b)));
        if (sort.dir === "desc") arr.reverse();
        return arr;
    }, [items, sort.key, sort.dir, customAccessors]);

    return { sortedItems, sortKey: sort.key, sortDir: sort.dir, requestSort };
};
